import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    forwardRef,
    Input,
    OnDestroy,
    OnInit
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
    flattenLocationTree,
    getLocationNodeTypeLabel,
    LocationNodeCapability,
    LocationNodeTree,
    LocationSelection
} from '../../models/location-node';
import { LocationNodeService } from '../../services/location-node.service';

@Component({
    selector: 'app-location-scope-picker',
    templateUrl: './location-scope-picker.component.html',
    styleUrls: ['./location-scope-picker.component.scss'],
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => LocationScopePickerComponent),
        multi: true
    }],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class LocationScopePickerComponent implements ControlValueAccessor, OnInit, OnDestroy {
    @Input() label = 'Locations';
    @Input() placeholder = 'Select locations';
    @Input() required = false;

    nodes: LocationNodeTree[] = [];
    selectedIds: string[] = [];
    selections: LocationSelection[] = [];
    loading = false;
    disabled = false;

    private readonly destroy$ = new Subject<void>();
    private onChange: (value: LocationSelection[]) => void = () => undefined;
    private onTouched: () => void = () => undefined;

    constructor(
        private readonly locationNodeService: LocationNodeService,
        private readonly changeDetectorRef: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.loading = true;
        this.locationNodeService.getTree()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: tree => {
                    this.nodes = flattenLocationTree(tree).filter(node =>
                        (node.capabilities & LocationNodeCapability.CanBeSelectedAsScope) !== 0);
                    this.loading = false;
                    this.changeDetectorRef.markForCheck();
                },
                error: () => {
                    this.nodes = [];
                    this.loading = false;
                    this.changeDetectorRef.markForCheck();
                }
            });
    }

    writeValue(value: LocationSelection[] | null): void {
        this.selections = (value ?? []).map(item => ({
            nodeId: item.nodeId,
            includeDescendants: !!item.includeDescendants
        }));
        this.selectedIds = this.selections.map(item => item.nodeId);
        this.changeDetectorRef.markForCheck();
    }

    registerOnChange(fn: (value: LocationSelection[]) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(disabled: boolean): void {
        this.disabled = disabled;
        this.changeDetectorRef.markForCheck();
    }

    selectedIdsChanged(ids: string[]): void {
        const previous = new Map(this.selections.map(item => [item.nodeId, item]));
        this.selectedIds = ids ?? [];
        this.selections = this.selectedIds.map(nodeId => ({
            nodeId,
            includeDescendants: previous.get(nodeId)?.includeDescendants ?? false
        }));
        this.emitValue();
    }

    includeDescendantsChanged(nodeId: string, includeDescendants: boolean): void {
        this.selections = this.selections.map(item => item.nodeId === nodeId
            ? { ...item, includeDescendants }
            : item);
        this.emitValue();
    }

    selectedNode(selection: LocationSelection): LocationNodeTree | undefined {
        return this.nodes.find(node => node.id === selection.nodeId);
    }

    typeLabel(node: LocationNodeTree): string {
        return getLocationNodeTypeLabel(node.type);
    }

    optionIndent(node: LocationNodeTree): number {
        return Math.max(0, node.depth) * 14;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private emitValue(): void {
        this.onChange(this.selections.map(item => ({ ...item })));
        this.onTouched();
        this.changeDetectorRef.markForCheck();
    }
}
